import { DynamicField, DynamicSections, Facility, PaymentDetailType } from '@cargos/sprintpay-models';
import moment from 'moment';
import { Detail } from '../details';
import { UserPayments } from '../payment/payment';
import { UserProperties } from './user';

export class SchenkerUser implements UserProperties, UserPayments {
    constructor(
        private apis: Record<string, string>,
        public dynamicFields: DynamicField[] = [],
        public dynamicSections: DynamicSections[] = []
    ) {
        const paymentURL = this.apis['payments'];
        this.dynamicSections = [
            {
                id: 1,
                label: 'Schenker Customer Reference',
                orderNumber: 0,
                company: { id: 57, name: 'Schenker' },
                emailDomain: null,
            },
            {
                id: 2,
                label: 'Payment Details',
                orderNumber: 1,
                company: { id: 57, name: 'Schenker' },
                emailDomain: null,
            },
            {
                id: 3,
                label: 'Customer Reference',
                orderNumber: 2,
                company: { id: 57, name: 'Schenker' },
                emailDomain: null,
            },
        ];
        this.dynamicFields = [
            {
                name: 'costCenter',
                label: 'Cost Center',
                section: 0,
                fieldType: 'input',
                company: undefined,
                emailDomain: null,
                isDisabled: false,
                isReplacement: false,
                isHidden: false,
                subLabel: '',
                tooltipTitle: '',
                tooltipMessage: '',
                validations: [
                    {
                        id: 0,
                        name: 'pattern',
                        value: '/^[a-zA-Z0-9]*$/',
                        message: 'We allow only alphanumeric characters. ',
                        title: null,
                        type: 'VALIDATION',
                    },
                    {
                        id: 1,
                        name: 'required',
                        value: 'true',
                        message: 'Required field',
                        title: null,
                        type: 'VALIDATION',
                    },
                    {
                        id: 2,
                        name: 'incorrect',
                        value: 'false',
                        apiValidator: `${paymentURL}/isValidCostCenter?{{company}}&{{name}}`,
                        message:
                            'Oops. The Cost Center format is not valid, enter as 2 Alpha + 5 Numeric + 2 alpha + 1 numeric.',
                        title: null,
                        type: 'VALIDATION',
                    },
                ],
            },
            {
                name: 'voucher',
                label: 'Schenker Voucher #',
                section: 0,
                fieldType: 'input',
                company: undefined,
                emailDomain: null,
                isDisabled: false,
                isReplacement: false,
                isHidden: false,
                subLabel: '',
                tooltipTitle: '',
                tooltipMessage: '',
                validations: [
                    {
                        id: 0,
                        name: 'pattern',
                        value: [/^(5(\d{8}|\d{9})|6(\d{8}|\d{9})|9[0-5][0-9][0-9][0-9][0-9][0-9][0-9][0-9])$/],
                        message:
                            "Oops. The Voucher # should start with a '5' (for USA offices) or a '6' (for Canada offices) and must be all numeric, with mininum 9 characters and maximum 10 characters. Or may start with a '9' (must be a number between 900000000 and 959999999)",
                        title: null,
                        type: 'VALIDATION',
                    },
                    {
                        id: 1,
                        name: 'required',
                        value: 'true',
                        message: 'Required field',
                        title: null,
                        type: 'VALIDATION',
                    },
                ],
            },
            {
                name: 'locationId',
                label: 'Location',
                section: 0,
                fieldType: 'dropdown',
                company: undefined,
                emailDomain: null,
                isDisabled: false,
                isReplacement: false,
                isHidden: false,
                subLabel: '',
                tooltipTitle: '',
                tooltipMessage: '',
                apiProviderData: `${paymentURL}/customer/approval-flow/information`,
                validations: [
                    {
                        id: 1,
                        name: 'required',
                        value: 'true',
                        message: 'Required field',
                        title: null,
                        type: 'VALIDATION',
                    },
                ],
            },
            {
                name: 'customerRef',
                label: 'File / Shipment / Archive #',
                section: 2,
                fieldType: 'input',
                company: undefined,
                emailDomain: null,
                isDisabled: false,
                isReplacement: false,
                isHidden: false,
                subLabel: '',
                tooltipTitle: '',
                tooltipMessage:
                    'Your own personal reference # so you can remember this payment. For example your file #.',
                validations: [
                    {
                        id: 0,
                        name: 'pattern',
                        value: [
                            /^\d{10}$/,
                            /^[a-zA-z]{5}\d{9}$/,
                            /^[a-zA-z]{7}\d{6}$/,
                            /^SH-(\d{9}|\d{10}|\d{11}|\d{12})$/,
                        ],
                        message:
                            'Enter as either 5 alpha + 9 numeric, or 10 numeric, or 7 alpha + 6 numeric, or "SH-" + 9-12 numeric',
                        title: null,
                        type: 'VALIDATION',
                    },
                    {
                        id: 1,
                        name: 'maxlength',
                        value: '255',
                        message: 'Maximum length exceeded (255 characters maximum)',
                        title: null,
                        type: 'VALIDATION',
                    },
                    {
                        id: 2,
                        name: 'required',
                        value: 'true',
                        message: 'Please enter your File / Shipment / Archive #.',
                        title: null,
                        type: 'VALIDATION',
                    },
                ],
            },
        ];
    }

    public getPaymentDetails = (payment: PaymentDetailType, facility: Facility, id: string): Detail[] => [
        {
            label: facility.externalLookup ? 'File Number' : 'Second facility reference #',
            value: payment.hawb ? payment.hawb : 'N/A',
            show: !facility.externalLookup && !id,
        },
        { label: 'Paid To', value: payment.paidTo ? payment.paidTo : 'N/A', show: true },
        {
            label: 'Cargo facility',
            value: facility.name ? facility.name : 'N/A',
            show: true,
        },
        {
            label: 'Amount',
            value: payment.amount ? '$' + payment.amount.toFixed(2) : 'N/A',
            show: true,
        },
    ];

    public getPaymentCustomerReference = (
        payment: PaymentDetailType,
        customerReferenceLabel: string = ''
    ): Detail[] => [
        {
            label: customerReferenceLabel ? customerReferenceLabel : (this.dynamicFields[3]?.label ?? ''),
            value: payment.customerReference ? payment.customerReference : 'N/A',
            show: true,
        },
        {
            label: 'Payment Notification Email',
            value: payment.notificationEmail ? payment.notificationEmail : 'N/A',
            show: true,
        },
        { label: 'Notes', value: payment.notes ? payment.notes : 'N/A', show: true },
    ];

    public getCustomerInformation = (payment: PaymentDetailType): Detail[] => [
        {
            label: 'Customer Name',
            value: payment.customer ? payment.customer.name : 'N/A',
            show: true,
        },
        {
            label: 'Notification email',
            value: payment?.notificationEmail ? payment?.notificationEmail : 'N/A',
            show: true,
        },
        {
            label: 'Created Date',
            value: payment?.createdDate ? moment(payment?.createdDate).format('MMM DD, YYYY') : 'N/A',
            show: true,
        },
    ];

    public getCompanyCustomerReference = (payment: PaymentDetailType): Detail[] => [
        {
            label: 'Cost Center',
            value: payment?.externalData?.costCenter ?? 'N/A',
            show: true,
        },
        {
            label: 'Voucher #',
            value: payment?.externalData?.voucher ?? 'N/A',
            show: !!(payment.externalLookup && payment.id),
        },
        {
            label: 'Payment Amount',
            value: payment?.externalData?.type ?? 'N/A',
            show: !!(payment?.externalLookup && payment.id),
        },
        {
            label: 'Type',
            value: payment?.paymentType ?? 'N/A',
            show: !!(payment?.externalLookup && payment.id),
        },
        { label: 'Location', value: payment?.location ?? 'N/A', show: true },
    ];
}
